import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Hammer, Play, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ago } from "@/lib/mongoMcp";
import BuildCheckSteps from "@/components/buildcheck/BuildCheckSteps";
import BuildCheckHistory from "@/components/buildcheck/BuildCheckHistory";

const STATE = {
    passed: ["All checks passed", "text-emerald-300 border-emerald-400/40 bg-emerald-400/10"],
    failed: ["Failing", "text-red-300 border-red-400/40 bg-red-500/10"],
    running: ["Running", "text-amber-200 border-amber-300/40 bg-amber-300/10"],
    interrupted: ["Interrupted", "text-amber-200 border-amber-300/40 bg-amber-300/10"],
    never: ["Not run yet", "text-white/60 border-white/15 bg-white/5"],
    off: ["Off on this server", "text-white/60 border-white/15 bg-white/5"],
};

export default function BuildCheckPage() {
    const [data, setData] = useState(null);
    const [denied, setDenied] = useState(false);
    const load = useCallback(async () => {
        try { setData((await api.get("/admin/build-check")).data); }
        catch (e) {
            if (e?.response?.status === 403) setDenied(true);
            else toast.error(formatApiErrorDetail(e));
        }
    }, []);
    const fast = data?.state === "running" || data?.pending || data?.run_requested;
    useEffect(() => {
        if (denied) return undefined;
        load();
        const t = setInterval(load, fast ? 4000 : 15000);
        return () => clearInterval(t);
    }, [load, fast, denied]);
    const runNow = async () => {
        try { await api.post("/admin/build-check/run"); toast("Build check starts within a few seconds"); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e)); }
    };
    const [label, tone] = STATE[data?.state] || STATE.never;
    return (
        <div className="p-8 space-y-6 max-w-6xl" data-testid="build-check-page">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    <Hammer className="w-5 h-5 text-white/70 mt-1" strokeWidth={1.5} />
                    <div>
                        <h1 className="font-display text-4xl tracking-tight">Build checks</h1>
                        <p className="text-sm text-white/50 mt-1 max-w-2xl">
                            Every check of the GitHub build runs here automatically a minute after changes stop. A change is only done when everything is green.
                        </p>
                    </div>
                </div>
                {data?.enabled && (
                    <div className="flex gap-2">
                        <button onClick={load} className="px-3 py-2 text-xs rounded-md border border-white/15 hover:bg-white/5 transition-colors" data-testid="build-check-refresh">
                            <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={runNow} disabled={data.state === "running" || data.run_requested} data-testid="build-check-run-now"
                            className="px-3 py-2 text-xs rounded-md bg-white text-black hover:bg-white/90 disabled:opacity-40 inline-flex items-center gap-1.5 transition-colors">
                            <Play className="w-3.5 h-3.5" /> {data.run_requested ? "Starting…" : "Run now"}
                        </button>
                    </div>
                )}
            </div>
            {denied ? <div className="text-sm text-white/60" data-testid="build-check-denied">Build checks are visible to super admins only.</div>
                : !data ? <div className="text-xs text-white/40 font-mono" data-testid="build-check-loading">Loading…</div> : (
                <>
                    <StatusBar data={data} label={label} tone={tone} />
                    {data.enabled && <BuildCheckSteps steps={data.steps || []} total={data.total} running={data.state === "running"} />}
                    {data.enabled && <BuildCheckHistory history={data.history} />}
                </>
            )}
        </div>
    );
}

function StatusBar({ data, label, tone }) {
    const pending = data.pending === "waiting" ? `Files changed — next run starts about ${data.starts_in_s}s after the last edit`
        : data.pending === "queued" ? "Files changed — next run starts within a few seconds" : null;
    return (
        <div className={`rounded-md border px-5 py-4 ${tone}`} data-testid="build-check-status">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                <span className="font-display text-xl tracking-tight" data-testid="build-check-state">
                    {label}{data.state === "running" ? ` · ${(data.steps || []).length}/${data.total || "?"}` : ""}
                </span>
                {data.started_at && <span className="text-[11px] font-mono text-white/60">started {ago(data.started_at)} · {data.trigger}</span>}
                {data.finished_at && <span className="text-[11px] font-mono text-white/60">finished {ago(data.finished_at)}</span>}
                {data.enabled && data.up_to_date && <span className="text-[11px] text-white/60" data-testid="build-check-up-to-date">covers the current files</span>}
            </div>
            {data.reason && <div className="text-xs text-white/60 mt-1" data-testid="build-check-off-reason">{data.reason}</div>}
            {pending && <div className="text-xs text-amber-200 mt-1.5" data-testid="build-check-pending">{pending}</div>}
            {data.changed?.length > 0 && (
                <div className="text-[11px] font-mono text-white/50 mt-1.5 break-words" data-testid="build-check-changed">changed: {data.changed.join(", ")}</div>
            )}
        </div>
    );
}
